// modules
import { useState, useEffect, useMemo, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// constants
import { RETRO_CLAIM as Constants, Routes, StatusInfo } from "constants/global";
import SearchFilterIcon from "components/atoms/SearchFilterIcon/SearchFilterIcon";

// Redux
import {
	getGroupPermissionList,
	groupPermissionListSelector,
	toggleFilter,
	showHideFilter,
	updateCurrentGroup,
	getUserPermissionListSelector,
	loadingGroupPermissionSelector
} from "redux/userList/userSlice";
import { resetClaimState, retroClaimListSelector } from "redux/retroClaim/retroClaimSlice";
// Hooks
import { useAppDispatch, useAppSelector } from "app/hooks/hooks";

// Types
import { GroupPermissionItem } from "containers/NewGroup/useNewGroup";
import { StatusBarInfoProps } from "containers/ReimbursementVerification/ReimbursementVerification";
import { FilterItem } from "constants/retrospectiveTypes";

// styles
import styles from "containers/RoleManagement/roleManagementComponent.module.css";

export type RetrospectiveClaimPageProps = {
	getGroupHeader: (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: any) => void;
	generateCols: () => void;
	handleExpandRows: (val: string) => void;
	loading: boolean;
	currentExpandedGroup: string | undefined;
	updatedClaimGroupList?: Array<GroupPermissionItem>;
	statusBarInfo: StatusBarInfoProps;
	defaultClaimGroupKey: string;
	handleCreateClaim: () => void;
};

export function useRetrospectiveClaimPage(): RetrospectiveClaimPageProps {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	const { data: groupPermissionData } = useAppSelector(groupPermissionListSelector);
	const data = useMemo(() => {
		return groupPermissionData?.filter((item: any) => item.perm_group_code === Constants.DEFAULT_GROUP_CODE.perm_group_code);
	}, [groupPermissionData]);

	const userPermissionList = useAppSelector(getUserPermissionListSelector);
	const showFilter = useAppSelector(showHideFilter);
	const claimList = useAppSelector(retroClaimListSelector);
	const groupsLoading = useAppSelector(loadingGroupPermissionSelector) === 1;

	const [groupSearchEntered, setGroupSearchEntered] = useState(false);
	const [currentExpandedGroup, setCurrentExpandedGroup] = useState<string>();

	const filterData: FilterItem = {};

	const claimListCount = useMemo(() => {
		return claimList?.[userPermissionList.filter((item) => item.perm_group_code === Constants.PERMISSION_CODE && item.perm_val2 > 0)[0]?.perm_val2]?.record_count;
	}, [userPermissionList, claimList]);

	useEffect(() => {
		if (!data) {
			dispatch(getGroupPermissionList(Constants.DEFAULT_GROUP_CODE));
		}
		dispatch(resetClaimState());
	}, []);

	const updatedClaimGroupList = useMemo(() => {
		if (data && data.length > 0) {
			const permissionData = userPermissionList.filter((item) => item.perm_group_code === Constants.PERMISSION_CODE && item.perm_val2 > 0)[0];
			if (permissionData) {
				Constants.CLAIMS_PENDING_ACTION_GROUP.perm_val1 = permissionData.perm_val1;
				Constants.CLAIMS_PENDING_ACTION_GROUP.perm_val2 = permissionData.perm_val2;
				dispatch(updateCurrentGroup(Constants.CLAIMS_PENDING_ACTION_GROUP.perm_code));
				return [Constants.CLAIMS_PENDING_ACTION_GROUP, ...data];
			}
			dispatch(updateCurrentGroup(data[0].perm_code));
			return [...data];
		}
	}, [data, groupSearchEntered, setGroupSearchEntered, pathname]);

	const defaultClaimGroupKey = updatedClaimGroupList?.[0].perm_code;

	const handleToggleFilter = (filterValues: FilterItem, groupCode: string) => {
		filterValues[groupCode] = !showFilter[groupCode];
		dispatch(updateCurrentGroup(groupCode));
		dispatch(toggleFilter(filterValues));
	};

	const statusBarInfo = useMemo((): StatusBarInfoProps => {
		if (claimListCount) {
			return {
				message:
					claimListCount > 1 ? (
						<p>
							There are currently <u>{claimListCount}</u> pending retrospective claims
						</p>
					) : (
						<p>
							There is currently <u>{claimListCount}</u> pending retrospective claim
						</p>
					),
				background: "yellow"
			};
		} else {
			return {
				message: <p>{StatusInfo.NO_ACTION_REQ}</p>,
				background: "green"
			};
		}
	}, [claimListCount]);

	const getGroupHeader = (_voucherTypeHandler?: () => void, _voucherDownloadHandler?: () => void, groupData?: Array<any>) => {
		return (
			data &&
			groupData &&
			groupData.map((item: any) => {
				const key = item?.perm_code;
				const sectionHeader = item.perm_id ? "Claims " + item.perm_short_desc : item.perm_short_desc;
				const isPendingClaimsGroup = item.perm_code === Constants.PENDING_GROUP_NAME;

				// Set default filter values based on group type
				const defaultFilterValues = isPendingClaimsGroup
					? ["Status (Pending first),Status (Pending Approver first)", Constants.DEFAULT_STATUS[1].value]
					: [Constants.DEFAULT_STATUS[0].value, Constants.DEFAULT_STATUS[1].value];

				return {
					cell1: <p className={styles.groupTitle}>{sectionHeader}</p>,
					cell2: (
						<SearchFilterIcon
							defaultSelectedFilter={Constants.DEFAULT_STATUS}
							defaultSelectedFilterValues={defaultFilterValues}
							label={"FILTER"}
							groupCode={item.perm_code}
							onClick={() => handleToggleFilter(filterData, item.perm_code)}
							useRetroClaimFilter={true}
							isGroupPendingClaims={isPendingClaimsGroup}
						/>
					),
					key: key,
					id: key
				};
			})
		);
	};

	const generateCols = () => {
		return [
			{
				dataIndex: "cell1",
				title: <p></p>,
				width: "90%"
			},
			{
				dataIndex: "cell2",
				title: <p></p>
			}
		];
	};

	const handleExpandRows = useCallback(
		(_value: string) => {
			setCurrentExpandedGroup(_value);
		},
		[currentExpandedGroup]
	);

	const handleCreateClaim = useCallback(() => {
		dispatch(resetClaimState());
		navigate(Routes?.CREATE_RETRO_CLAIM);
	}, [dispatch, navigate]);

	return {
		loading: !data && groupsLoading,
		defaultClaimGroupKey,
		updatedClaimGroupList,
		getGroupHeader,
		generateCols,
		statusBarInfo,
		handleExpandRows,
		currentExpandedGroup,
		handleCreateClaim
	};
}
